import { useState } from "react";
import { X, Plus, Trash2, Layers } from "lucide-react";
import toast from "react-hot-toast";
import SelectMenu from "./crm/SelectMenu";
import {
  METRIC_FIELDS,
  SUBCARD_FIELDS,
  getFieldDef,
  TIME_UNITS,
  DEFAULT_ACCENT,
  DEFAULT_BG,
  countForCard,
  groupForSubcard,
} from "../utils/metricEngine";

// Sensible default filter object for a freshly-picked field.
function defaultFilterFor(fieldKey) {
  const def = getFieldDef(fieldKey);
  if (!def) return { field: fieldKey, op: "is", value: "" };
  switch (def.kind) {
    case "date":
      return { field: fieldKey, mode: "window", direction: "next", n: 7, unit: "Day" };
    case "category":
      return { field: fieldKey, op: "has", value: "" };
    case "text":
      return { field: fieldKey, op: "contains", value: "" };
    default:
      return { field: fieldKey, op: "is", value: "" };
  }
}

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-[#0A061E] text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#C99F4A]";

/**
 * Shared add/edit form for a metric card.
 *
 * props:
 *   mode      "add" | "edit"
 *   initial   existing card object (edit) or null
 *   labels    [{ id, name }]
 *   cases     all cases (for the live preview count) – optional
 *   uid       current user id (for category/preview) – optional
 *   onClose   () => void
 *   onSubmit  (formData) => Promise<void>
 *   hideFilters  hide the Filters section (built-in dashboard cards — their
 *                case list is fixed, only appearance/breakdown are editable)
 */
export default function MetricCardForm({ mode = "add", initial, labels = [], cases = [], uid, onClose, onSubmit, hideFilters = false }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [accentColor, setAccentColor] = useState(initial?.accentColor || DEFAULT_ACCENT);
  const [bgColor, setBgColor] = useState(initial?.bgColor || DEFAULT_BG);
  const [filters, setFilters] = useState(() => {
    if (Array.isArray(initial?.filters) && initial.filters.length) return initial.filters;
    // Convert a legacy single-field card into the new filter shape for editing.
    if (initial?.field) {
      const map = {
        caseStatus: [{ field: "status", op: "is", value: initial.caseStatus }],
        hearingInSevenDays: [{ field: "nextHearingDate", mode: "window", direction: "next", n: 7, unit: "Day" }],
        "created on": [{ field: "caseFiledDate", mode: "on", value: initial.date }],
        hasNextDate: [{ field: "nextHearingDate", mode: "exists" }],
        byCategory: [{ field: "label", op: "has", value: initial.categoryId }],
      };
      return map[initial.field] || [];
    }
    return [];
  });
  const [multilevel, setMultilevel] = useState(!!initial?.multilevel);
  const [subcardField, setSubcardField] = useState(initial?.subcardField || "");
  const [saving, setSaving] = useState(false);

  const labelsMap = Object.fromEntries(labels.map((l) => [l.id, l.name]));
  const previewCard = { title, accentColor, bgColor, filters, multilevel, subcardField };
  const previewCount = cases.length ? countForCard(cases, previewCard, { uid }) : null;
  const previewSubcards =
    multilevel && subcardField && cases.length
      ? groupForSubcard(cases, previewCard, { uid }, labelsMap)
      : { groups: [], more: 0 };

  const addFilter = () => setFilters((f) => [...f, defaultFilterFor("status")]);
  const removeFilter = (i) => setFilters((f) => f.filter((_, idx) => idx !== i));
  const patchFilter = (i, patch) =>
    setFilters((f) => f.map((flt, idx) => (idx === i ? { ...flt, ...patch } : flt)));
  const changeField = (i, fieldKey) =>
    setFilters((f) => f.map((flt, idx) => (idx === i ? defaultFilterFor(fieldKey) : flt)));

  const validate = () => {
    if (!title.trim()) return "Please enter a display name.";
    for (const flt of filters) {
      const def = getFieldDef(flt.field);
      if (!def) continue;
      if (def.kind === "date") {
        if (flt.mode === "on" && !flt.value) return "Pick a date for the date filter.";
        if (flt.mode === "window" && (!flt.n || Number(flt.n) <= 0)) return "Enter a valid time-window length.";
      } else if (def.kind === "category") {
        if (!flt.value) return "Select a label / category.";
      } else if (!String(flt.value || "").trim()) {
        return `Enter a value for "${def.label}".`;
      }
    }
    if (multilevel && !subcardField) return "Pick a field to break the card down by.";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        accentColor,
        bgColor,
        filters,
        accumulator: "count",
        multilevel,
        subcardField: multilevel ? subcardField : "",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-[#1A1535] rounded-2xl shadow-2xl w-full max-w-3xl text-white max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <h2 className="text-xl font-bold mb-5 pr-8">
            {mode === "edit" ? "Edit Metric Card" : "Add Metric Card"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* ---- Left: form ---- */}
            <div className="md:col-span-2 space-y-5">
              <div>
                <label className="block mb-1 text-sm font-medium">Display Name</label>
                <input
                  className={inputCls}
                  placeholder="e.g. Hearings This Week"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium">Description</label>
                <textarea
                  className={inputCls}
                  rows={2}
                  placeholder="Short description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Appearance */}
              <div>
                <p className="text-sm font-semibold mb-2">Appearance</p>
                <div className="grid grid-cols-2 gap-3">
                  <ColorField label="Accent Color" value={accentColor} onChange={setAccentColor} />
                  <ColorField label="Background" value={bgColor} onChange={setBgColor} />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Accent drives the top stripe and value colour. Background tints the card.
                </p>
              </div>

              {/* Filters */}
              {!hideFilters && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Filters</p>
                  <button
                    type="button"
                    onClick={addFilter}
                    className="flex items-center gap-1 text-xs text-[#C99F4A] hover:text-yellow-300"
                  >
                    <Plus size={14} /> Add Filter
                  </button>
                </div>

                {filters.length === 0 && (
                  <p className="text-xs text-gray-400 mb-2">
                    No filters — this card counts <span className="text-white">all</span> cases. Add a filter to narrow it down.
                  </p>
                )}

                <div className="space-y-3">
                  {filters.map((flt, i) => (
                    <FilterRow
                      key={i}
                      filter={flt}
                      labels={labels}
                      onField={(k) => changeField(i, k)}
                      onPatch={(p) => patchFilter(i, p)}
                      onRemove={() => removeFilter(i)}
                    />
                  ))}
                </div>
              </div>
              )}

              {/* Multilevel */}
              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={multilevel}
                    onChange={(e) => setMultilevel(e.target.checked)}
                    className="accent-[#C99F4A]"
                  />
                  <Layers size={15} /> Make this a multilevel card (break down by a field)
                </label>
                {multilevel && (
                  <div className="mt-2">
                    <SelectMenu
                      className={inputCls}
                      value={subcardField}
                      onChange={(e) => setSubcardField(e.target.value)}
                    >
                      <option value="">Break down by…</option>
                      {SUBCARD_FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>{f.label}</option>
                      ))}
                    </SelectMenu>
                  </div>
                )}
              </div>
            </div>

            {/* ---- Right: live preview ---- */}
            <div className="md:col-span-1">
              <p className="text-sm font-semibold mb-2">Preview</p>
              <PreviewCard
                title={title || "Untitled card"}
                accentColor={accentColor}
                bgColor={bgColor}
                count={previewCount}
                subcards={previewSubcards.groups}
                subcardsMore={previewSubcards.more}
              />
              {previewCount != null && (
                <p className="text-xs text-gray-400 mt-2">
                  Live count from your {cases.length} case{cases.length === 1 ? "" : "s"}.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-[#C99F4A] hover:bg-[#b88f3f] text-black font-semibold text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : mode === "edit" ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Sub-components --------------------------------------------------------

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className="block mb-1 text-xs text-gray-300 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 rounded bg-transparent border border-white/10 cursor-pointer"
        />
        <input
          className={inputCls}
          placeholder="#0D9488"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

function FilterRow({ filter, labels, onField, onPatch, onRemove }) {
  const def = getFieldDef(filter.field);
  return (
    <div className="bg-[#0A061E]/60 rounded-lg p-3 border border-white/5">
      <div className="flex items-center gap-2 mb-2">
        <SelectMenu
          className={inputCls}
          value={filter.field}
          onChange={(e) => onField(e.target.value)}
        >
          {METRIC_FIELDS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </SelectMenu>
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-red-400 shrink-0"
          aria-label="Remove filter"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Field-specific controls */}
      {def?.kind === "select" && (
        <div className="grid grid-cols-2 gap-2">
          <SelectMenu className={inputCls} value={filter.op || "is"} onChange={(e) => onPatch({ op: e.target.value })}>
            <option value="is">is</option>
            <option value="isNot">is not</option>
          </SelectMenu>
          <SelectMenu className={inputCls} value={filter.value || ""} onChange={(e) => onPatch({ value: e.target.value })}>
            <option value="">-- select --</option>
            {def.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </SelectMenu>
        </div>
      )}

      {def?.kind === "text" && (
        <div className="grid grid-cols-2 gap-2">
          <SelectMenu className={inputCls} value={filter.op || "contains"} onChange={(e) => onPatch({ op: e.target.value })}>
            <option value="contains">contains</option>
            <option value="is">is exactly</option>
          </SelectMenu>
          <input
            className={inputCls}
            placeholder="value"
            value={filter.value || ""}
            onChange={(e) => onPatch({ value: e.target.value })}
          />
        </div>
      )}

      {def?.kind === "category" && (
        <SelectMenu className={inputCls} value={filter.value || ""} onChange={(e) => onPatch({ value: e.target.value })}>
          <option value="">-- select label --</option>
          {labels.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </SelectMenu>
      )}

      {def?.kind === "date" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {[
              ["window", "Time Window"],
              ["on", "Specific Date"],
              ["exists", "Has Value"],
              ["missing", "No Value"],
            ].map(([m, lbl]) => (
              <label key={m} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  className="accent-[#C99F4A]"
                  checked={(filter.mode || "window") === m}
                  onChange={() => onPatch({ mode: m })}
                />
                {lbl}
              </label>
            ))}
          </div>

          {(filter.mode || "window") === "window" && (
            <div className="flex items-center gap-2">
              <SelectMenu className={inputCls} value={filter.direction || "next"} onChange={(e) => onPatch({ direction: e.target.value })}>
                <option value="last">Last</option>
                <option value="next">Next</option>
              </SelectMenu>
              <input
                type="number"
                min="1"
                className={`${inputCls} w-20`}
                value={filter.n ?? 7}
                onChange={(e) => onPatch({ n: e.target.value })}
              />
              <SelectMenu className={inputCls} value={filter.unit || "Day"} onChange={(e) => onPatch({ unit: e.target.value })}>
                {TIME_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </SelectMenu>
            </div>
          )}

          {filter.mode === "on" && (
            <input
              type="date"
              className={`custom-date ${inputCls}`}
              value={filter.value || ""}
              onChange={(e) => onPatch({ value: e.target.value })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PreviewCard({ title, accentColor, bgColor, count, subcards, subcardsMore = 0 }) {
  const bg = /^#[0-9a-fA-F]{6}$/.test(bgColor) ? bgColor : "#FFFFFF";
  const accent = /^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : DEFAULT_ACCENT;
  return (
    <div
      className="rounded-lg p-4 shadow-lg"
      style={{ background: bg, borderTop: `4px solid ${accent}` }}
    >
      <p className="text-sm font-medium text-gray-800 mb-1 break-words">{title}</p>
      <p className="text-3xl font-bold" style={{ color: accent }}>
        {count == null ? "—" : count}
      </p>
      {subcards.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 mt-3">
          {subcards.map((s) => (
            <div key={s.key} className="bg-black/5 rounded px-2 py-1">
              <p className="text-[10px] text-gray-600 truncate" title={s.label}>{s.label}</p>
              <p className="text-sm font-bold text-gray-800">{s.value}</p>
            </div>
          ))}
          {subcardsMore > 0 && (
            <div className="bg-black/5 rounded px-2 py-1 flex items-center justify-center">
              <p className="text-[11px] font-semibold text-gray-600">+{subcardsMore} more</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
